// Sorting a failed request into the ONE question every Homebase surface has to answer: is this an
// expected business outcome we can explain, or a genuine unexpected error?
//
// The distinction is not cosmetic. `FriendlyError` is the unexpected-error primitive: it always
// renders "Something went wrong — the team has been notified" and treats its `detail` as
// engineering text that PRODUCTION DROPS. Feeding it a mapped human sentence therefore hides that
// sentence from every production user — the reason the reschedule screen read as a crash, and the
// reason the post-session feedback screen did too.
//
// This module owns the mechanism and nothing else; each surface supplies its own outcome copy and
// its own discriminator (`reschedule/rescheduleOutcome.ts` maps backend codes,
// `flows/feedback/feedbackOutcome.ts` maps a status). There is deliberately no second
// implementation of the classification itself.

import { ApiError } from "@/api/client";

/**
 * An EXPECTED outcome of a request — a business answer, not a failure.
 *
 * These render as an ordinary explanatory screen in EVERY environment. They must never go
 * through `FriendlyError`: that primitive is for UNEXPECTED failures, always renders
 * "something went wrong", and treats its `detail` as engineering text that prod DROPS — so a
 * sentence handed to it is a sentence a production user never reads.
 */
export interface ExpectedOutcome {
  /** The serif headline. States what happened; never the word "error". */
  title: string;
  /** The explanation, shown to every user in every environment. */
  body: string;
  /**
   * The one move that still works. Per-outcome on purpose: telling someone to contact their
   * therapist because a slot list went stale would be worse advice than telling them nothing.
   * Takes the therapist's name because only some outcomes route there.
   */
  nextStep: (therapistName: string) => string;
}

export type ExpectedFailure<TOutcome extends ExpectedOutcome = ExpectedOutcome> =
  /** A recognised outcome: explain it, in every environment. */
  | { kind: "expected"; outcome: TOutcome }
  /** Anything else: `FriendlyError`, unchanged — including the prod detail suppression. */
  | { kind: "unexpected"; detail: string | null };

/**
 * The server's `detail` on a failed response, as text, or `null` when there isn't one.
 *
 * Two wire shapes, both real: most endpoints return a bare-string `detail` (a
 * `ClientRescheduleError` code, or a plain sentence like "Feedback window is not open"), and the
 * reschedule accept path's slot race returns `{code, remaining_slots}`. Read both so a caller
 * can't miss a reason because of where it sat.
 */
export function apiErrorDetail(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const detail = (error.data as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  if (detail !== null && typeof detail === "object") {
    const code = (detail as { code?: unknown }).code;
    if (typeof code === "string") return code;
  }
  return null;
}

/**
 * The text an UNEXPECTED failure reports into `FriendlyError`'s off-prod debug block.
 *
 * `ApiError.message` ALONE is not enough, and the operator's screenshot is the proof: it read
 * "HTTP 409:" with the reason blank. The message is built from `status` + `statusText`, and
 * `statusText` is empty over HTTP/2 (the protocol has no reason phrase), so the status line is
 * all that survives and the server's `detail` — the only part that says WHY — never reached the
 * screen even on staging. Append it whenever there is one.
 *
 * `fallbackDetail` is what a NON-`Error` rejection reports. It must never be `null`:
 * `FriendlyError` renders nothing at all for a null detail, which would turn a failed action into
 * a NON-EVENT — the user clicks, nothing happens, and no error is ever shown. An empty string
 * still renders the reassuring line (just no debug block), so the floor is "something was
 * reported", never silence.
 */
export function unexpectedDetail(error: unknown, fallbackDetail = ""): string {
  if (!(error instanceof Error)) return fallbackDetail;
  // `error.message` ends in a trailing space whenever `statusText` is empty, which is the common
  // case — join on the non-empty parts so the block reads "HTTP 500: reason", not "HTTP 500:  ".
  return [error.message.trim(), apiErrorDetail(error)].filter(Boolean).join(" ");
}

/** The `detail` to hand `FriendlyError` — `null` (render nothing) for an expected outcome. */
export function unexpectedErrorDetail(failure: ExpectedFailure | null): string | null {
  return failure?.kind === "unexpected" ? failure.detail : null;
}
